/**
 * Export registry. Every format is written from the same machine pattern, read back with an
 * independent parser in this module, and compared with the expected canonical stitch list
 * before the file is offered for download.
 */
import { t } from '../i18n/i18n';
import type { Issue } from '../engine/model/issues';
import type { Design } from '../engine/model/types';
import { CMD, type StitchSequence } from '../engine/stitch/sequence';
import { exportOrigin } from '../engine/stitch/stats';
import { type CanonicalOptions, canonical, compareCanonical } from './compare';
import { DST_LIMITS, readDst, toAscii, writeDst } from './dst';
import { EXP_LIMITS, readExp, writeExp } from './exp';
import { JEF_LIMITS, readJef, writeJef } from './jef';
import { type FormatLimits, type MachinePattern, type MachineStitch, countCommands, extents, toMachinePattern } from './machine';
import { PES_LIMITS, readPes, writePes, writePes6 } from './pes';
import { VP3_LIMITS, readVp3, writeVp3 } from './vp3';
import { FormatError } from './bytes';

export type FormatId = 'dst' | 'exp' | 'pes' | 'pes6' | 'jef' | 'vp3';

export interface FormatInfo {
  id: FormatId;
  name: string;
  extension: string;
  machines: string;
  limits: FormatLimits;
  colors: 'none' | 'palette' | 'rgb';
  trims: 'native' | 'jump-sequence' | 'machine-setting';
  canonical: CanonicalOptions;
  /** User-facing notes about what the format cannot represent. */
  notes: string[];
  write: (p: MachinePattern) => Uint8Array;
  read: (bytes: Uint8Array) => MachineStitch[];
}

export const FORMATS: Record<FormatId, FormatInfo> = {
  dst: {
    id: 'dst',
    name: 'Tajima DST',
    extension: 'dst',
    machines: 'Tajima and most industrial machines',
    limits: DST_LIMITS,
    colors: 'none',
    trims: 'jump-sequence',
    canonical: {},
    notes: [
      'DST does not store thread colours – only colour changes. Choose the colours on the machine.',
      'Trims are written as three jumps with no net movement (Tajima convention).',
    ],
    write: writeDst,
    read: (b) => readDst(b).stitches,
  },
  exp: {
    id: 'exp',
    name: 'Melco EXP',
    extension: 'exp',
    machines: 'Melco/Bernina and compatibles',
    limits: EXP_LIMITS,
    colors: 'none',
    trims: 'native',
    canonical: {},
    notes: ['EXP does not store thread colours – only colour changes.'],
    write: writeExp,
    read: readExp,
  },
  pes: {
    id: 'pes',
    name: 'Brother PES (v1)',
    extension: 'pes',
    machines: 'Brother and Baby Lock',
    limits: PES_LIMITS,
    colors: 'palette',
    trims: 'native',
    canonical: {},
    notes: ['Colours are stored as the nearest colour in Brother\'s 64-colour PEC palette.'],
    write: writePes,
    read: (b) => readPes(b).pec.stitches,
  },
  pes6: {
    id: 'pes6',
    name: 'Brother PES v6 (RGB)',
    extension: 'pes',
    machines: 'Brother and Baby Lock, PE-Design 6 and newer',
    limits: PES_LIMITS,
    colors: 'rgb',
    trims: 'native',
    canonical: {},
    notes: [
      'PES v6 stores the exact thread colours and names in the file. The machine sews from the PEC part, which shows the nearest Brother colour.',
      'Use PES v1 if an older machine or program cannot open the file.',
    ],
    write: writePes6,
    read: (b) => readPes(b).pec.stitches,
  },
  jef: {
    id: 'jef',
    name: 'Janome JEF',
    extension: 'jef',
    machines: 'Janome, Elna and Kenmore',
    limits: JEF_LIMITS,
    colors: 'palette',
    trims: 'machine-setting',
    canonical: { dropTrims: true },
    notes: [
      'Colours are stored as the nearest colour in Janome\'s palette.',
      'JEF has no trim command; the machine trims jumps according to its own setting.',
    ],
    write: (p) => writeJef(p),
    read: (b) => readJef(b).stitches,
  },
  vp3: {
    id: 'vp3',
    name: 'Pfaff/Viking VP3',
    extension: 'vp3',
    machines: 'Pfaff and Husqvarna Viking',
    limits: VP3_LIMITS,
    colors: 'rgb',
    trims: 'native',
    canonical: { dropJumps: true },
    notes: ['VP3 has no jump command; moves are stored as long displacements (normally right after a trim).'],
    write: writeVp3,
    read: (b) => readVp3(b).stitches,
  },
};

export const FORMAT_IDS = Object.keys(FORMATS) as FormatId[];

export interface ExportSummary {
  stitches: number;
  jumps: number;
  trims: number;
  colorChanges: number;
  widthMm: number;
  heightMm: number;
}

export interface ExportResult {
  ok: boolean;
  format: FormatInfo;
  bytes: Uint8Array | null;
  fileName: string;
  issues: Issue[];
  /** Summary of the file as read back by the independent parser. */
  readBack: ExportSummary | null;
}

export function summarize(stitches: MachineStitch[]): ExportSummary {
  const e = extents(stitches.filter((s) => s.cmd === CMD.STITCH));
  return {
    stitches: countCommands(stitches, CMD.STITCH),
    jumps: countCommands(stitches, CMD.JUMP),
    trims: countCommands(stitches, CMD.TRIM),
    colorChanges: countCommands(stitches, CMD.COLOR_CHANGE),
    widthMm: (e.maxX - e.minX) / 10,
    heightMm: (e.maxY - e.minY) / 10,
  };
}

export function safeFileName(name: string): string {
  // ASCII only: many machines cannot show other characters in file names
  const base = toAscii(name).replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_');
  return base || 'design';
}

/** Builds the machine pattern for a format from the design's stitch sequence. */
export function machinePatternFor(seq: StitchSequence, design: Design, format: FormatId): MachinePattern {
  return toMachinePattern(seq, exportOrigin(seq, design.machine), FORMATS[format].limits, design.name);
}

/** Writes, re-reads and validates a file. Never returns bytes that failed validation. */
export function exportFormat(seq: StitchSequence, design: Design, formatId: FormatId, blockingIssues: Issue[] = []): ExportResult {
  const format = FORMATS[formatId];
  const fileName = `${safeFileName(design.name)}.${format.extension}`;
  const issues: Issue[] = [];
  const fail = (message: string): ExportResult => {
    issues.push({ severity: 'error', code: 'export', message });
    return { ok: false, format, bytes: null, fileName, issues, readBack: null };
  };
  const errors = blockingIssues.filter((i) => i.severity === 'error');
  if (errors.length) {
    issues.push(...errors);
    return fail(t('{n} problem(s) in the design must be fixed before export.', { n: errors.length }));
  }
  let stitchCount = 0;
  for (let i = 0; i < seq.count; i++) if (seq.cmds[i] === CMD.STITCH) stitchCount++;
  if (stitchCount === 0) return fail(t('The design has no stitches to export. Add objects or digitize an image.'));

  const pattern = machinePatternFor(seq, design, formatId);
  for (const s of pattern.stitches) {
    if (Math.abs(s.x) > format.limits.maxCoord || Math.abs(s.y) > format.limits.maxCoord) {
      return fail(t('The design is too large for {format}: coordinate ({x} mm, {y} mm) exceeds the format limit of ±{max} mm.', { format: format.name, x: s.x / 10, y: s.y / 10, max: format.limits.maxCoord / 10 }));
    }
  }
  if (formatId === 'pes' || formatId === 'jef' || formatId === 'vp3') {
    const e = extents(pattern.stitches);
    if (e.maxX - e.minX > 65535 || e.maxY - e.minY > 65535) return fail(t('The design is too large for {format}.', { format: format.name }));
  }
  if (formatId === 'pes' && pattern.threads.length > 255) return fail(t('PES supports at most 255 colour blocks.'));

  let bytes: Uint8Array;
  try {
    bytes = format.write(pattern);
  } catch (e) {
    return fail(t('Could not write {format}: {error}', { format: format.name, error: e instanceof Error ? e.message : String(e) }));
  }
  let readBack: MachineStitch[];
  try {
    readBack = format.read(bytes);
  } catch (e) {
    return fail(t('Verification read of {format} failed: {error}', { format: format.name, error: e instanceof FormatError || e instanceof Error ? e.message : String(e) }));
  }
  const cmp = compareCanonical(canonical(pattern.stitches, format.canonical), canonical(readBack, format.canonical));
  if (!cmp.equal) return fail(t('Validation of {format} failed: {details}.', { format: format.name, details: cmp.differences.join('; ') }));
  for (const note of format.notes) issues.push({ severity: 'info', code: 'format-note', message: t(note) });
  return { ok: true, format, bytes, fileName, issues, readBack: summarize(readBack) };
}
